/**
 * DevstudioWorkspace — split layout: chat panel | iframe preview.
 *
 * Drží session state, posílá zprávy přes SSE stream, parsuje diff bloky
 * z odpovědí a zobrazuje DiffViewer + TurnHistory.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button, Select, StatusDot } from '../ui';
import { api } from '../../lib/api';
import { apiProxyUrl } from '../../lib/service-endpoints';
import { DiffViewer } from './DiffViewer';
import { TurnHistory } from './TurnHistory';

const API = '/apiext/devstudio';

export interface Turn {
  id: string;
  session_id: string;
  turn_index: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  diff_unified: string | null;
  state: 'proposed' | 'applying' | 'applied' | 'reverted' | 'failed';
  commit_sha: string | null;
  parent_sha: string | null;
  revert_of: string | null;
  deploy_status: 'pending' | 'healthy' | 'broken' | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

interface SessionDetail {
  ok: boolean;
  session: {
    id: string;
    project_id: string;
    project_slug: string | null;
    preview_url: string | null;
    last_active: string;
  };
  turns: Turn[];
}

interface LlmProfile {
  id: string;
  label: string;
  backend: string;
  enabled: boolean;
  priority: number;
}

interface Props {
  sessionId: string;
  projectId: string;
}

export function DevstudioWorkspace({ sessionId, projectId }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [profileId, setProfileId] = useState('');
  const [iframeKey, setIframeKey] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  // Načti session detail (turny + meta)
  const session = useQuery<SessionDetail>({
    queryKey: ['devstudio-session', sessionId],
    queryFn: () =>
      api.mcp<SessionDetail>('devstudio.session.get', { session_id: sessionId }),
    refetchInterval: streaming ? false : 5_000,
  });

  // LLM profily pro dropdown — catalog-backed přes llm-profiles MCP
  const profiles = useQuery<LlmProfile[]>({
    queryKey: ['devstudio-llm-profiles'],
    queryFn: async () => {
      const r = await api.mcp<{
        profiles: Array<{
          id: string;
          label?: string | null;
          backend?: string | null;
          enabled: boolean;
          priority?: number;
        }>;
      }>('llm-profiles.list', { enabled_only: true });
      return (r.profiles ?? []).map((p) => ({
        id: p.id,
        label: p.label || p.id,
        backend: p.backend || 'openai',
        enabled: p.enabled,
        priority: p.priority ?? 0,
      }));
    },
    staleTime: 60_000,
  });

  // Reload iframe po každém applied turnu
  const turns = session.data?.turns ?? [];
  const lastApplied = turns
    .filter((t) => t.state === 'applied' || t.state === 'reverted')
    .at(-1);

  useEffect(() => {
    if (lastApplied?.deploy_status === 'healthy') {
      setIframeKey((k) => k + 1);
    }
  }, [lastApplied?.id, lastApplied?.deploy_status]);

  async function sendMessage() {
    if (!input.trim() || streaming) return;

    const message = input.trim();
    setInput('');
    setStreaming(true);
    setStreamText('');

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      // SSE stream — jdeme přes proxy stejně jako api.ts, jen ručně kvůli streaming
      const res = await fetch(apiProxyUrl(`${API}/sessions/${sessionId}/stream`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ message, profile_id: profileId || undefined }),
        signal: ctrl.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`Stream failed: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6)) as { type: string; text?: string };
            if (evt.type === 'text' && evt.text) {
              setStreamText((s) => s + evt.text);
            }
            if (evt.type === 'done' || evt.type === 'error') {
              await qc.invalidateQueries({ queryKey: ['devstudio-session', sessionId] });
            }
          } catch { /* ignore parse */ }
        }
      }
    } catch (err) {
      console.error('[devstudio] stream error', err);
    } finally {
      setStreaming(false);
      setStreamText('');
      abortRef.current = null;
      await qc.invalidateQueries({ queryKey: ['devstudio-session', sessionId] });
    }
  }

  function abortStream() {
    abortRef.current?.abort();
  }

  async function applyTurn(turnId: string) {
    await api.mcp('devstudio.diff.apply', { turn_id: turnId });
    await qc.invalidateQueries({ queryKey: ['devstudio-session', sessionId] });
    setIframeKey((k) => k + 1);
  }

  async function revertTurn(turnId: string) {
    await api.mcp('devstudio.turn.revert', { turn_id: turnId });
    await qc.invalidateQueries({ queryKey: ['devstudio-session', sessionId] });
    setIframeKey((k) => k + 1);
  }

  const previewUrl = session.data?.session.preview_url;

  return (
    <div className="flex flex-1 min-h-0">
      {/* LEFT: Chat panel */}
      <div className="flex flex-col w-[480px] border-r border-zinc-800 min-h-0">
        {/* Toolbar */}
        <div className="flex items-center gap-2 p-2 border-b border-zinc-800 text-xs">
          <span className="opacity-60">Model:</span>
          <Select
            value={profileId}
            onChange={(e) => setProfileId(e.target.value)}
            className="flex-1"
          >
            <option value="">(default)</option>
            {profiles.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label} ({p.backend})
              </option>
            ))}
          </Select>
        </div>

        {/* Messages + diffs + history */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          <TurnHistory
            turns={turns}
            onApply={applyTurn}
            onRevert={revertTurn}
            DiffViewer={DiffViewer}
          />

          {streaming && streamText && (
            <div className="text-sm border border-blue-500/40 rounded p-2 bg-blue-500/5">
              <div className="text-xs opacity-60 mb-1">streaming…</div>
              <ReactMarkdown>{streamText}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-2 border-t border-zinc-800">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('devstudio.inputPlaceholder', 'Co má AI změnit? (Ctrl+Enter = poslat)')}
            className="w-full h-20 bg-zinc-900 border border-zinc-700 rounded p-2 text-sm resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void sendMessage();
              }
            }}
          />
          <div className="flex justify-end mt-1 gap-2">
            {streaming
              ? (
                <Button variant="warning" onClick={abortStream}>
                  <Loader2 className="animate-spin" size={14} />
                  {t('devstudio.abort', 'Přerušit')}
                </Button>
              )
              : (
                <Button variant="primary" onClick={() => void sendMessage()} disabled={!input.trim()}>
                  <Send size={14} />
                  {t('devstudio.send', 'Odeslat')}
                </Button>
              )}
          </div>
        </div>
      </div>

      {/* RIGHT: Preview iframe */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center gap-2 p-2 border-b border-zinc-800 text-xs">
          <StatusDot status={lastApplied?.deploy_status === 'healthy' ? 'running' : 'pending'} />
          <span className="opacity-60">Preview:</span>
          <span className="font-mono">{previewUrl ?? t('devstudio.previewUnavailable', '(nedostupné)')}</span>
          <Button
            variant="ghost"
            onClick={() => setIframeKey((k) => k + 1)}
            className="ml-auto"
            title="Reload iframe"
          >
            <RefreshCw size={14} />
          </Button>
        </div>
        {previewUrl
          ? (
            <iframe
              key={iframeKey}
              src={previewUrl}
              className="flex-1 bg-white"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              title={`preview ${projectId}`}
            />
          )
          : (
            <div className="flex-1 flex items-center justify-center text-sm opacity-50">
              {t('devstudio.previewMissing', 'Preview URL není k dispozici. Zkontroluj orch_status pro projekt.')}
            </div>
          )}
      </div>
    </div>
  );
}
